import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import brand from './brandConfig';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Kassekladde from './pages/Kassekladde';
import QuickEntry from './pages/QuickEntry';
import Accounts from './pages/Accounts';
import Vouchers from './pages/Vouchers';
import VoucherForm from './pages/VoucherForm';
import VoucherDetail from './pages/VoucherDetail';
import Members from './pages/Members';
import Activities from './pages/Activities';
import ActivityDetail from './pages/ActivityDetail';
import IncomeStatement from './pages/IncomeStatement';
import BalanceSheet from './pages/BalanceSheet';
import ImportData from './pages/ImportData';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import OpeningBalance from './pages/OpeningBalance';
import Invoices from './pages/Invoices';
import InvoiceForm from './pages/InvoiceForm';
import InvoiceDetail from './pages/InvoiceDetail';
import MembershipBilling from './pages/MembershipBilling';
import Export from './pages/Export';
import Postings from './pages/Postings';
import BankImport from './pages/BankImport';
import SuperAdmin from './pages/SuperAdmin';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Indlæser...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Indlæser...</div>;
  if (user) return <Navigate to="/app" />;
  return children;
}

function SuperAdminRedirect() {
  const { user } = useAuth();
  if (user?.role === 'superadmin') return <Navigate to="/app/superadmin" />;
  return <Dashboard />;
}

function BrandInit() {
  useEffect(() => {
    document.title = brand.meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', brand.meta.description);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', brand.primaryColor);
    document.querySelector('link[rel="icon"]')?.setAttribute('href',
      `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='20' fill='${encodeURIComponent(brand.primaryColor)}'/><text x='50' y='68' font-size='52' font-weight='800' text-anchor='middle' fill='white' font-family='system-ui'>${brand.initials}</text></svg>`
    );
    document.documentElement.setAttribute('data-brand', brand.id);
  }, []);
  return null;
}

function App() {
  return (
    <BrandProvider>
    <AuthProvider>
      <BrandInit />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/opret" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<SuperAdminRedirect />} />
            <Route path="superadmin" element={<SuperAdmin />} />
            <Route path="kassekladde" element={<Kassekladde />} />
            <Route path="hurtig" element={<QuickEntry />} />
            <Route path="kontoplan" element={<Accounts />} />
            <Route path="aabningsbalance" element={<OpeningBalance />} />
            <Route path="bilag" element={<Vouchers />} />
            <Route path="bilag/ny" element={<VoucherForm />} />
            <Route path="bilag/:id" element={<VoucherDetail />} />
            <Route path="fakturaer" element={<Invoices />} />
            <Route path="fakturaer/ny" element={<InvoiceForm />} />
            <Route path="fakturaer/:id" element={<InvoiceDetail />} />
            <Route path="kontingent" element={<MembershipBilling />} />
            <Route path="medlemmer" element={<Members />} />
            <Route path="aktiviteter" element={<Activities />} />
            <Route path="aktiviteter/:id" element={<ActivityDetail />} />
            <Route path="resultat" element={<IncomeStatement />} />
            <Route path="balance" element={<BalanceSheet />} />
            <Route path="import" element={<ImportData />} />
            <Route path="eksport" element={<Export />} />
            <Route path="posteringer" element={<Postings />} />
            <Route path="bankimport" element={<BankImport />} />
            <Route path="indstillinger" element={<Settings />} />
            <Route path="aendringslog" element={<AuditLog />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </BrandProvider>
  );
}

export default App;
